<template>
  <div class="loadingwrapper flex items-center justify-center min-vh-100" v-if="loading">
    <Loading class="loading" />
  </div>
  <div class="content-add-route" v-else>
    <LicenceSourceModalWrapper ref="licensingsrcmodal" />
    <BreadcrumbNavConnected class="mb4" />
    <ContentBlock>
      <template #instructions>
        <h5>{{ $t('Add a Title Manually') }}</h5>
        <p>{{ $t('Select a content preset with pre-filled content details or enter content details from scratch.') }}</p>
      </template>
    </ContentBlock>
    <div :class="['center w-80-m flex-l items-center justify-center', `w-${classElement}-l`]"  style="float:left">
      <UIFormItem
        class="flex-auto w-50-l mb4 mb0-m"
        :root="true"
        :title="$t('Add content from SynthesisVR')"
        :instructions="$t('Add content from SynthesisVR by entering the ID of the content you want to add. This will pre-fill the details for you.')"
      >
        <div class="flex">
          <UIInput
            class="span-3 w-100"
            v-model="synthesisId"
            :value="synthesisId"
          />
          <UIButton
            class="addbtn"
            :disabled="!synthesisId"
            @click="createNewEntryFromSynthesys"
          >{{$t('Add')}}</UIButton>
        </div>
      </UIFormItem>
      <div class="ph6 tc flex items-center db-l" v-if="!onlySyntesis">
        <span class="separator db w-50 h3-l b--geyser"></span>
        <span class="dib f-tiny slate pv4 ph4 ttu">{{$t('or')}}</span>
        <span class="separator db w-50 h3-l b--geyser"></span>
      </div>
      <UIFormItem
        class="flex-auto w-50-l mb4 mb0-m"
        :root="true"
        :title="$t('Select a Content Preset')"
        :instructions="$t('Select from our growing database of titles, and use our pre-filled details for images, videos, descriptions and launch parameters.')"
        v-if="!onlySyntesis"
      >
        <div class="flex">
          <UIMultiselect
            class="tagselector flex-auto"
            :placeholder="$t('Search for content here')"
            :value="selectedPreset"
            id="presets"
            label="title"
            track-by="id"
            open-direction="bottom"
            :options="presets"
            :searchable="true"
            :loading="isLoading"
            :internal-search="false"
            :close-on-select="true"
            :options-limit="300"
            :max-height="600"
            :show-no-results="true"
            :show-no-options="false"
            :hide-selected="true"
            @search-change="searchPresets"
            @select="selectPreset"
            @close="presets = []; isLoading = false"
          >
            <template #option="{ option }">
              <div class="flex items-center">
                <div class="w-25 mr2">
                  <RatioContainer :ratio="46.5863">
                    <img :src="option.imageUrl" />
                  </RatioContainer>
                </div>
                <h6 class="flex-auto truncate">{{ option.title }}</h6>
              </div>
            </template>
            <template #noResult>{{ $t('No presets found.') }}</template>
          </UIMultiselect>
          <UIButton
            class="addbtn"
            :disabled="!selectedPreset"
            @click="createNewEntryFromPreset"
          >{{$t('Add')}}</UIButton>
        </div>
      </UIFormItem>
      <div class="ph6 tc flex items-center db-l" v-if="!onlySyntesis">
        <span class="separator db w-50 h3-l b--geyser"></span>
        <span class="dib f-tiny slate pv4 ph4 ttu">{{$t('or')}}</span>
        <span class="separator db w-50 h3-l b--geyser"></span>
      </div>
      <UIFormItem
        class="flex-auto w-50-l"
        :root="true"
        :instructions="$t('This option will require you to input all of the content’s details and information.')"
        v-if="!onlySyntesis"
      >
        <template #title>&nbsp;</template>
        <UIButton
          secondary
          class="w-100"
          @click="createNewEntry"
        >{{$t('Create a Custom Content Entry')}}</UIButton>
      </UIFormItem>
      <BottomBar :backOnly="true" :backRouterLink="{ name: 'content-library' }" />
    </div>
  </div>
</template>

<script>
import Component, { mixins } from 'vue-class-component';
import FocusModeMixin from '@/core/shared/misc/FocusModeMixin';
import ContentBlock from '@/core/shared/components/ContentBlock';
import UIFormItem from '@/core/shared/components/ui/UIFormItem';
import UIButton from '@/core/shared/components/ui/UIButton';
import UIMultiselect from '@/core/shared/components/ui/UIMultiselect';
import BreadcrumbNavConnected from '@/core/shared/components/_connected/BreadcrumbNavConnected';
import BottomBar from '@/core/shared/components/BottomBar';
import { createModalWrapper } from '@/core/shared/helpers/HOCHelper';
import LicenceSourceModal from '@/content/library/components/LicenceSourceModal';
import RatioContainer from '@/core/shared/components/utils/RatioContainer';
import UIInput from '@/core/shared/components/ui/UIInput.vue';
import Loading from '@/core/shared/components/Loading';
const LicenceSourceModalWrapper = createModalWrapper(LicenceSourceModal);
@Component({
  components: {
    UIInput,
    ContentBlock,
    UIFormItem,
    UIButton,
    UIMultiselect,
    BottomBar,
    RatioContainer,
    LicenceSourceModalWrapper,
    BreadcrumbNavConnected,
    Loading,
  },
})
export default class ContentAddRoute extends mixins(FocusModeMixin) {
  presets = []
  isLoading = false
  selectedPreset = null
  synthesisId = null
  onlySyntesis = false;
  loading = false;
  classElement = 100;
  created () {
    this.$store.commit('setTitle', this.$t('Add a Title Manually'));
    this.setBreadcrumb();
  }

  mounted () {
    const { mode, search } = this.$route.query;
    if (mode === 'synthesis' && search) {
      this.synthesisId = search;
      this.onlySyntesis = true;
      this.classElement = 25;
    }
    document.querySelectorAll('span.label').forEach(el => {
      if (el.textContent.trim() === 'Add content from SynthesisVR') {
        el.innerHTML = `Add content from SynthesisVR
      <a href="https://svr.li/k/477" target="_blank" style="float: right;">
        Check the full Knowledge Base guide
      </a>`;
      }
    });
  }

  selectPreset (val) {
    this.selectedPreset = val;
  }

  async searchPresets (query) {
    if (!query) return;
    this.isLoading = true;
    this.presets = [];
    try {
      const games = await this.$store.dispatch('searchPresets', query);
      if (games) this.presets = games;
    } finally {
      this.isLoading = false;
    }
  }

  createNewEntryFromPreset () {
    this.$refs.licensingsrcmodal.show(null, {
      submit: async ({ licensingSource }) => {
        this.$store.commit('contentNew/setLicensingSource', licensingSource);
        await this.$store.dispatch('contentNew/prefillWithPreset', this.selectedPreset);
        this.$router.push({ name: 'content-new' });
      },
    });
  }

  async createNewEntryFromSynthesys () {
    this.loading = true;
    try {
      const game = await this.$store.dispatch('searchGameFromSynthesisVr', {
        synthesisId: this.synthesisId,
        organizationId: this.$store.state.organization.currentOrganizationId,
      });
      if (game.status === 0) {
        this.loading = false;
        this.$store.commit('setFlash', {
          type: 'error',
          message: 'Game with ID ' + this.synthesisId + ' is not existing or not available.',
        });
      } else {
        const gameInfo = game.data[this.synthesisId];
        const formatData = {
          id: this.synthesisId,
          title: gameInfo.title || null,
          ageRating: gameInfo.agerating,
          imageUrl: gameInfo.image || null,
          videoUrl: gameInfo.trailer || null,
          description: gameInfo.description || null,
          difficulty: gameInfo.difficulty || 2,
          organizationTags: [],
          singleplayer: !gameInfo.multiplayer,
          multiplayer: gameInfo.multiplayer,
          arguments: null,
          filePath: null,
          launchMethod: 'uri',
          uri: gameInfo.uri || null,
        };
        const gameId = await this.$store.dispatch('contentNew/createGameFromSynthesisVr', formatData);
        const organizationTags = [];
        for (const tag of gameInfo.tags) {
          const tagId = await this.$store.dispatch('contentNew/storeNewTagForGame', {
            tag: tag,
            gameId: gameId,
          });
          organizationTags.push({
            id: tagId.id,
            text: tag,
          });
        }
        formatData.id = gameId;
        formatData.organizationTags = organizationTags;
        this.$store.commit('contentNew/setLicensingSource', 'other');
        this.$store.commit('contentNew/setSynthesisVrId', this.synthesisId);
        await this.$store.dispatch('contentNew/prefillWithSynthesisVr', formatData);
        this.$router.push({ name: 'content-new' });
        this.loading = false;
      }
    } catch (error) {
      this.loading = false;
    }
  }

  createNewEntry () {
    this.$refs.licensingsrcmodal.show(null, {
      submit: ({ licensingSource }) => {
        this.$store.commit('contentNew/setLicensingSource', licensingSource);
        this.$router.push({ name: 'content-new' });
      },
    });
  }

  setBreadcrumb () {
    this.$store.commit('setBreadcrumbItems', [
      {
        name: this.$t('My Library'),
        routerLink: { name: 'content-library' },
      },
      {
        name: this.$t('Add a Title Manually'),
      },
    ]);
  }
}
</script>

<style scoped lang="postcss">
@import "core/shared/styles";

.content-add-route {
  @apply --container;
  padding-top: var(--spacingSm);
  @media (--desktop) {
    padding-top: var(--spacingMd);
  }
  & .separator {
    border-style: solid;
    border-bottom-width: 1px;
    @media (--tablet) {
      border-bottom-width: 0;
      border-right-width: 1px;
    }
  }
  & .tagselector {
    z-index: 201;
    position: relative;
  }
  & .tagselector >>> .multiselect__tags {
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
  }
  & .addbtn {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
  }
}
</style>
